/**
 * useIntakeWizard — state machine for the requirements-intake wizard.
 *
 * State machine:
 *   idle → starting → answering → preview → deduping → confirming → done
 *                 rehydrating ↗ (when a session id is in params/storage)
 *             rehydrateError   (GET failed; offers Retry + "Start fresh")
 *
 * Phase B (always active):
 * - Rehydrate-on-mount from params.sessionId or localStorage
 * - URL sync: navigate to /requests/new/:id once a session exists
 * - localStorage sync: activeSessionId + draftAnswers (debounced)
 * - Per-step error surface (no terminal global 'error' status)
 * - retry() — re-invokes the last failed action / calls retryStep
 * - discardCurrent() — soft-abandon + clear storage + navigate away
 *
 * Phase C (behind VITE_INTAKE_STREAMING=true):
 * - start() and submitAnswers()/finalizeNow() drive via SSE streams
 * - handleIntakeEvent() reducer maps events to WizardState idempotently
 * - On stream done/error/abnormal-end → reconcile via intake.get(id)
 * - Rehydrate with running step → open catchup stream, then reconcile
 * - dedupe + confirm remain the existing REST path (never streamed)
 * - Flag OFF → identical REST behaviour as Phase B
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError, intake, intakeStream } from '@/helpers/ApiHelper';
import { useAuth } from '@/contexts/AuthContext';
import { isIntakeStreamingEnabled } from '@/lib/featureFlags';
import { useAttachmentUploads, type AttachmentItem } from '@/hooks/useAttachmentUploads';
import {
  clearDraftAnswers as storageClearDraftAnswers,
  readActiveSessionId,
  readDraftAnswers,
  useIntakeDraftStorage,
  type DraftAnswerState,
} from '@/hooks/useIntakeDraftStorage';
import type {
  DetectProject,
  IntakeAnswerItem,
  IntakeConfirmResponse,
  IntakeDedupeCandidateItem,
  IntakeSSEEvent,
  IntakeSessionState,
  RequirementPreview,
} from '@/types/intake';

// ---------------------------------------------------------------------------
// Feature flag — Phase C streaming (default OFF)
// ---------------------------------------------------------------------------
// isIntakeStreamingEnabled() is imported from @/lib/featureFlags so that tests
// can mock it reliably via vi.mock('@/lib/featureFlags') — which works in CI
// unlike vi.stubEnv (Vite statically replaces import.meta.env.VITE_* at
// transform time in CI, making runtime stubbing ineffective there).

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStatus =
  | 'idle'
  | 'rehydrating'
  | 'rehydrateError'
  | 'starting'
  | 'answering'
  | 'preview'
  | 'deduping'
  | 'confirming'
  | 'done';

export interface WizardStepError {
  /** Which wizard phase (action name) failed. */
  phase: string;
  message: string;
  retriable: boolean;
}

export interface WizardState {
  status: WizardStatus;
  session: IntakeSessionState | null;
  dedupeCandidates: IntakeDedupeCandidateItem[] | null;
  confirmResult: IntakeConfirmResponse | null;
  /** Per-step inline error — does NOT change the active screen. Cleared on retry/reset. */
  error: WizardStepError | null;
  /** True when the session has been rehydrated but questions haven't been loaded yet */
  rehydrateError: string | null;
  /**
   * Phase C — live coverage progress text driven by `coverage_update` SSE events.
   * Null when the flag is off or no coverage event has arrived yet.
   * e.g. "4 covered · 3 remaining"
   */
  coverageNote: string | null;
  /**
   * Phase C — true while a streaming step is running (between step_started and
   * question_ready / preview_ready / step_failed). Used to show skeleton cards.
   */
  stepRunning: boolean;
}

export interface UseIntakeWizard {
  state: WizardState;
  /**
   * The un-submitted answers for the current round, seeded from localStorage
   * on rehydration. Only non-null while status === 'answering'.
   */
  restoredAnswers: DraftAnswerState | null;
  /** Open a new intake session with the supplied free-text request + target project(s). */
  start: (initialRequest: string, projects: DetectProject[], files?: File[]) => Promise<void>;
  /** Attachments for the current session (uploaded, uploading, or failed). */
  attachments: AttachmentItem[];
  /** Upload more files to the current session. */
  addAttachments: (files: File[]) => void;
  /** Delete a server attachment / drop a failed one. */
  removeAttachment: (key: string) => void;
  /** Re-run a failed upload. */
  retryAttachment: (key: string) => void;
  /**
   * Submit answers for the current question round.
   * Pass finalize=true to skip remaining rounds and synthesize the preview now.
   * Pass an idempotency_key to enable safe replay on retry.
   */
  submitAnswers: (
    answers: IntakeAnswerItem[],
    finalize?: boolean,
    idempotency_key?: string,
  ) => Promise<void>;
  /** Shortcut: submit the current round's answers with finalize=true. */
  finalizeNow: (answers: IntakeAnswerItem[], idempotency_key?: string) => Promise<void>;
  /** PATCH derived preview fields from the preview card. */
  updatePreview: (patch: Partial<RequirementPreview>) => Promise<void>;
  /** PATCH the targeted project(s) from the preview card. */
  updateProjects: (projects: DetectProject[]) => Promise<void>;
  /** Submit from the preview: runs the duplicate check transparently. */
  submit: () => Promise<void>;
  /** Return from the duplicate-check interrupt back to the editable preview. */
  backToPreview: () => void;
  /** Confirm creation — pass null to create new, or a UUID to link-instead. */
  confirm: (linkToRequirementId: string | null) => Promise<void>;
  /** Retry the last failed action, re-using the exact same payload. */
  retry: () => Promise<void>;
  /**
   * Soft-abandon the current session: DELETE on the server + clear storage
   * + navigate back to /requests/new (bare).
   */
  discardCurrent: () => Promise<void>;
  /** Reset back to idle so the user can start over (client-only, no server call). */
  reset: () => void;
  /** Notify the hook that the current round's answers changed so it can persist them. */
  onAnswersChange: (sessionId: string, state: DraftAnswerState) => void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: WizardState = {
  status: 'idle',
  session: null,
  dedupeCandidates: null,
  confirmResult: null,
  error: null,
  rehydrateError: null,
  coverageNote: null,
  stepRunning: false,
};

/**
 * Derive an inline error from a session that has no questions to show.
 *
 * Both the rehydrate and the post-stream reconcile paths can land on an
 * in_progress session whose round never produced questions. Rendering that as
 * the loading skeleton strands the user on a spinner forever, so the state has
 * to become an error with a Retry / Start over instead. Returns null when there
 * are questions to render (nothing is wrong).
 */
function stalledSessionError(session: IntakeSessionState): WizardStepError | null {
  if ((session.questions ?? []).length > 0) return null;

  const failedStep = session.failed_step_id
    ? session.steps.find((s) => s.step_id === session.failed_step_id)
    : undefined;

  if (failedStep) {
    return {
      phase: failedStep.kind,
      message: failedStep.error ?? session.last_error ?? 'The last step failed.',
      retriable: true,
    };
  }

  // No questions, no failed step: the stream ended before the round landed (a
  // dropped connection, or Cloud Run's 300s request cap cutting a slow round).
  // The runner may still be working server-side, so reloading can pick it up.
  // Not retriable: with no failed_step_id the retry endpoint has nothing to
  // re-run, so Retry would 409 (or silently no-op).
  return {
    phase: 'stream',
    message:
      session.last_error ??
      'The connection dropped before the questions arrived. Reload to pick up where it left off, or start over.',
    retriable: false,
  };
}

// ---------------------------------------------------------------------------
// Bounded client backoff for idempotent GETs
// ---------------------------------------------------------------------------

const MAX_GET_RETRIES = 2;
const RETRY_DELAYS_MS = [300, 900];

// ---------------------------------------------------------------------------
// Confirm-step polling constants
// ---------------------------------------------------------------------------

const CONFIRM_POLL_INTERVAL_MS = 1500;
const CONFIRM_POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 min; work continues server-side regardless

// Exported for direct unit testing. `timing` defaults to the module constants
// so the production callers (confirm / submit) stay unchanged; tests inject a
// tiny interval/timeout to exercise the deadline path without simulating the
// full 5-minute window (which is flaky to drive through ~200 fake-timer cycles).
export async function pollConfirmUntilTerminal(
  sessionId: string,
  handlers: {
    onConfirmed: (session: IntakeSessionState) => void;
    onFailed: (session: IntakeSessionState) => void;
    onDeadline: () => void;
  },
  timing: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<void> {
  const intervalMs = timing.intervalMs ?? CONFIRM_POLL_INTERVAL_MS;
  const timeoutMs = timing.timeoutMs ?? CONFIRM_POLL_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    await new Promise<void>((res) => setTimeout(res, intervalMs));
    let session: IntakeSessionState;
    try {
      session = await getWithRetry(sessionId);
    } catch {
      if (Date.now() > deadline) break;
      continue;
    }
    if (session.status === 'confirmed') { handlers.onConfirmed(session); return; }
    const confirmStep = [...session.steps].reverse().find((s) => s.kind === 'confirm');
    if (confirmStep?.status === 'failed') { handlers.onFailed(session); return; }
    if (Date.now() > deadline) break;
  }
  handlers.onDeadline();
}

async function getWithRetry(id: string): Promise<IntakeSessionState> {
  let lastError: Error = new Error('Unknown error');
  for (let attempt = 0; attempt <= MAX_GET_RETRIES; attempt++) {
    try {
      return await intake.get(id);
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e));
      const status = e instanceof ApiError ? e.status : 0;
      // Retry only transient errors — not 4xx (auth / not-found / forbidden).
      const isRetriable = status === 0 || status === 429 || status >= 500;
      if (!isRetriable || attempt === MAX_GET_RETRIES) throw lastError;
      await new Promise<void>((res) =>
        setTimeout(res, RETRY_DELAYS_MS[attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]),
      );
    }
  }
  throw lastError;
}

// ---------------------------------------------------------------------------
// SSE event reducer (Phase C)
// ---------------------------------------------------------------------------

/**
 * Map a single intake SSE event to the next WizardState.
 * All sets are wholesale/idempotent so catchup replay is safe.
 * Called inside setState() updaters.
 *
 * Exported so pure-reducer tests can call it directly without the hook —
 * this makes those tests env-independent (no flag, no stream mock, no React).
 */
export function handleIntakeEvent(prev: WizardState, evt: IntakeSSEEvent): WizardState {
  switch (evt.event) {
    case 'session_started': {
      // Session id is handled by the caller (URL sync + storage).
      // Update status and seed a minimal session object so that subsequent
      // events (question_ready etc.) can merge into it.
      const newStatus = prev.status === 'starting' ? 'answering' : prev.status;
      const seedSession = prev.session ?? {
        id: evt.data.session_id,
        status: evt.data.status as import('@/types/intake').IntakeSessionStatus,
        initial_request: '',
        projects: [],
        round: 1,
        questions: null,
        derived: null,
        notion_task_id: null,
        notion_url: null,
        requirement_id: null,
        phase: 'extract',
        steps: [],
        last_error: null,
        failed_step_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        attachments: [],
      };
      return { ...prev, status: newStatus, session: seedSession };
    }

    case 'step_started':
      // Mark a step as running — show skeleton cards while in flight.
      return { ...prev, stepRunning: true, coverageNote: null };

    case 'step_progress':
      // Optional event — show the note if present but don't change the screen.
      return prev;

    case 'coverage_update': {
      const { covered, uncovered } = evt.data;
      const note = `${covered.length} covered · ${uncovered.length} remaining`;
      return { ...prev, coverageNote: note };
    }

    case 'question_ready':
      // Wholesale set questions for the current round; stay in answering.
      // evt.data.round_index is 0-based; session.round is kept as a 1-based
      // count (= len(rounds)) consistent with the REST path and draft-restore key.
      return {
        ...prev,
        status: 'answering',
        stepRunning: false,
        coverageNote: null,
        session: prev.session
          ? {
              ...prev.session,
              round: evt.data.round_index + 1,
              questions: evt.data.questions,
            }
          : prev.session,
      };

    case 'recommend_ready':
      // Backfill recommendation chips — always follows question_ready.
      // Merge without dropping questions (set semantics on the full list).
      return {
        ...prev,
        session: prev.session
          ? {
              ...prev.session,
              questions: evt.data.questions,
            }
          : prev.session,
      };

    case 'preview_ready':
      return {
        ...prev,
        status: 'preview',
        stepRunning: false,
        coverageNote: null,
        session: prev.session
          ? { ...prev.session, derived: evt.data.derived, status: 'preview' }
          : prev.session,
      };

    case 'step_failed':
      return {
        ...prev,
        stepRunning: false,
        error: {
          phase: evt.data.kind,
          message: evt.data.error,
          retriable: evt.data.retryable,
        },
      };

    case 'done':
      // Reconciliation happens after done in the caller (intake.get).
      // Mark stepRunning=false so the UI shows the last state cleanly.
      return { ...prev, stepRunning: false };

    default:
      return prev;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIntakeWizard(): UseIntakeWizard {
  const [state, setState] = useState<WizardState>(initialState);
  const [restoredAnswers, setRestoredAnswers] = useState<DraftAnswerState | null>(null);

  const navigate = useNavigate();
  const params = useParams<{ sessionId?: string }>();
  const { user } = useAuth();

  const storage = useIntakeDraftStorage(user?.id);
  const {
    items: attachmentItems,
    upload: uploadAttachments,
    retry: retryUpload,
    remove: removeUpload,
    hydrate: hydrateAttachments,
    clear: clearAttachments,
  } = useAttachmentUploads();

  // Track the last action so retry can re-invoke it.
  // ref so retrying doesn't trigger a re-render / effect loop.
  const lastActionRef = useRef<(() => Promise<void>) | null>(null);
  // Track idempotency key for the current answer submission.
  const lastAnswerPayloadRef = useRef<{
    answers: IntakeAnswerItem[];
    finalize: boolean;
    idempotency_key: string;
    round: number;
  } | null>(null);
  // Phase C — teardown ref for the active SSE stream. cancel() on unmount.
  const streamTeardownRef = useRef<(() => void) | null>(null);
  // The session id this hook last put in the URL. The rehydrate effect uses it to
  // tell its own URL syncs apart from navigation to a different draft (the Resume
  // banner), which must load that draft. Set before setState commits the session,
  // so a state-derived id would race the navigate.
  const syncedSessionIdRef = useRef<string | null>(null);

  // Tear down any active stream on unmount.
  useEffect(() => {
    return () => {
      if (streamTeardownRef.current) {
        streamTeardownRef.current();
        streamTeardownRef.current = null;
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const setStepError = useCallback((phase: string, message: string, retriable = true) => {
    setState((prev) => ({
      ...prev,
      error: { phase, message, retriable },
    }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // ---------------------------------------------------------------------------
  // Phase C — reconcile from REST GET (authoritative source of truth)
  // Called after any stream done/error/abnormal-end to re-derive wizard state.
  // ---------------------------------------------------------------------------

  const reconcileFromGet = useCallback(
    async (sessionId: string): Promise<void> => {
      try {
        const session = await getWithRetry(sessionId);
        hydrateAttachments(session.attachments ?? []);
        if (session.status === 'preview') {
          setState((prev) => ({
            ...prev,
            status: 'preview',
            session,
            stepRunning: false,
            coverageNote: null,
            error: prev.error, // preserve any step_failed error for the inline retry
          }));
        } else if (session.status === 'confirmed') {
          if (user?.id) storage.clearActiveSession();
          storageClearDraftAnswers(sessionId);
          setState({
            ...initialState,
            status: 'done',
            session,
            confirmResult: {
              session_id: session.id,
              status: 'confirmed',
              notion_task_id: session.notion_task_id,
              requirement_id: session.requirement_id,
              notion_url: session.notion_url ?? null,
              deduped_into: null,
            },
          });
        } else if (session.status === 'abandoned') {
          // Discard leaves the stream open, so its `done` reconciles here. Without
          // this the discarded session renders as answering again, now with a
          // stalled-session alert describing a round nobody is waiting for.
          if (user?.id) storage.clearActiveSession();
          storageClearDraftAnswers(sessionId);
          setState(initialState);
        } else {
          // in_progress or any other status → stay in answering
          setState((prev) => ({
            ...prev,
            status: 'answering',
            session,
            stepRunning: false,
            coverageNote: null,
            // A round that produced no questions has to surface as an error;
            // otherwise this parks on the loading skeleton indefinitely.
            error: prev.error ?? stalledSessionError(session),
          }));
        }
      } catch {
        // Reconciliation GET failed — keep the current UI state intact.
        // The inline error from step_failed (if any) is already visible.
        setState((prev) => ({ ...prev, stepRunning: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, storage, hydrateAttachments],
  );

  // ---------------------------------------------------------------------------
  // Phase C — open an SSE stream and drive the reducer
  // ---------------------------------------------------------------------------

  const openIntakeStream = useCallback(
    (
      request: Parameters<typeof intakeStream>[0],
      sessionIdForReconcile: string,
    ): void => {
      // Cancel any existing stream before opening a new one.
      if (streamTeardownRef.current) {
        streamTeardownRef.current();
        streamTeardownRef.current = null;
      }

      const cancel = intakeStream(request, {
        onEvent: (evt: IntakeSSEEvent) => {
          setState((prev) => handleIntakeEvent(prev, evt));
        },
        onNonStream: () => {
          // Server responded with non-SSE content (proxy, flag off server-side).
          // Fall back to REST — the session id is already set so the user can retry.
          setState((prev) => ({ ...prev, stepRunning: false }));
        },
        onHttpError: ({ status, detail }) => {
          if (status === 401) return; // handled globally by the fetch — redirect
          setState((prev) => ({
            ...prev,
            stepRunning: false,
            error: { phase: 'stream', message: detail, retriable: status >= 500 || status === 429 },
          }));
        },
        onStreamError: (err) => {
          setState((prev) => ({
            ...prev,
            stepRunning: false,
            error: { phase: 'stream', message: err.message, retriable: true },
          }));
        },
        onComplete: () => {
          streamTeardownRef.current = null;
          // Reconcile via GET — the authoritative source of truth.
          void reconcileFromGet(sessionIdForReconcile);
        },
      });

      streamTeardownRef.current = cancel;
    },
    [reconcileFromGet],
  );

  // URL sync — navigate to /requests/new/:id once a session id exists.
  const syncUrl = useCallback(
    (sessionId: string) => {
      const currentPath = window.location.pathname;
      const targetPath = `/requests/new/${sessionId}`;
      syncedSessionIdRef.current = sessionId;
      if (currentPath !== targetPath) {
        navigate(targetPath, { replace: true });
      }
    },
    [navigate],
  );

  // ---------------------------------------------------------------------------
  // Rehydrate-on-mount
  // ---------------------------------------------------------------------------

  // Rehydrate on mount, and again when the URL moves to a different draft — both
  // /requests/new routes render the same mounted page, so the banner's Resume
  // only changes params.sessionId. We rely SOLELY on the `cancelled` flag (set in the
  // cleanup) for StrictMode safety — a "run once" ref guard deadlocks under
  // React 18 StrictMode's mount→unmount→mount: run #1 gets cancelled by the
  // cleanup, run #2 is blocked by the ref, so neither completes and the wizard
  // hangs forever on the loading skeleton.
  useEffect(() => {
    // Our own syncUrl landing — the session is already loaded.
    if (params.sessionId && params.sessionId === syncedSessionIdRef.current) return;
    // The id left the URL after a session was loaded (Start over) — nothing to restore.
    if (!params.sessionId && syncedSessionIdRef.current !== null) return;

    const effectiveId =
      params.sessionId ?? (user?.id ? readActiveSessionId(user.id) : null);

    if (!effectiveId) return; // nothing to restore

    let cancelled = false;

    // Switching drafts: stop tailing the previous session's stream.
    streamTeardownRef.current?.();
    streamTeardownRef.current = null;
    setRestoredAnswers(null);

    const doRehydrate = async (): Promise<void> => {
      setState((prev) => ({ ...prev, status: 'rehydrating', rehydrateError: null }));

      let session: IntakeSessionState;
      try {
        session = await getWithRetry(effectiveId);
      } catch (e) {
        if (cancelled) return;
        const status = e instanceof ApiError ? e.status : 0;
        if (status === 403 || status === 404) {
          // Session gone or not ours — clear storage and start fresh.
          if (user?.id) storage.clearActiveSession();
          storageClearDraftAnswers(effectiveId);
          navigate('/requests/new', { replace: true });
          setState(initialState);
          return;
        }
        // Network / 5xx — offer Retry + "Start fresh".
        setState((prev) => ({
          ...prev,
          status: 'rehydrateError',
          rehydrateError: e instanceof ApiError ? e.detail : 'Failed to reload session',
        }));
        return;
      }

      if (cancelled) return;

      // Persist the session id so refreshes can restore it.
      if (user?.id) storage.saveActiveSession(session.id);
      syncUrl(session.id);
      // Resume: the attachment list repopulates from the session state.
      hydrateAttachments(session.attachments ?? []);

      if (session.status === 'confirmed') {
        // Already submitted — show a terminal card, clear storage.
        if (user?.id) storage.clearActiveSession();
        storageClearDraftAnswers(session.id);
        setState({
          ...initialState,
          status: 'done',
          session,
          confirmResult: {
            session_id: session.id,
            status: 'confirmed',
            notion_task_id: session.notion_task_id,
            requirement_id: session.requirement_id,
            notion_url: session.notion_url ?? null,
            deduped_into: null,
          },
        });
        return;
      }

      if (session.status === 'abandoned') {
        if (user?.id) storage.clearActiveSession();
        storageClearDraftAnswers(session.id);
        navigate('/requests/new', { replace: true });
        setState(initialState);
        return;
      }

      if (session.status === 'preview') {
        setState({ ...initialState, status: 'preview', session });
        return;
      }

      // in_progress — seed draftAnswers from localStorage if round matches.
      const stored = readDraftAnswers(session.id);
      const shouldRestore = stored !== null && stored.round === session.round;
      if (shouldRestore) setRestoredAnswers(stored);

      // Phase C — if there is a running step and streaming is enabled, open the
      // catchup stream so the user sees live progress. The reconcile on `done`
      // will set the final state. Skip when flag is off (REST path only).
      const hasRunningStep =
        isIntakeStreamingEnabled() &&
        session.steps.some((s) => s.status === 'running') &&
        session.failed_step_id == null;

      if (hasRunningStep) {
        // Show skeleton + spinner while we tail the live stream.
        setState({
          ...initialState,
          status: 'answering',
          session: { ...session, questions: null }, // null triggers skeleton
          stepRunning: true,
        });
        openIntakeStream({ mode: 'catchup', sessionId: session.id }, session.id);
      } else {
        // A session that died before producing questions must not rehydrate into
        // the bare loading skeleton: with no Alert there is no Retry and no Start
        // over, so the reload the user reaches for is exactly what strands them.
        setState({
          ...initialState,
          status: 'answering',
          session,
          error: stalledSessionError(session),
        });
      }
    };

    void doRehydrate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.sessionId]); // only the URL's draft id — the rest are read fresh per run

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const start = useCallback(
    async (initialRequest: string, projects: DetectProject[], files: File[] = []): Promise<void> => {
      setState({ ...initialState, status: 'starting' });
      clearError();
      lastActionRef.current = null;
      clearAttachments();

      if (isIntakeStreamingEnabled()) {
        // Phase C — drive via SSE stream.
        // We don't have a session id yet; it arrives via the session_started event.
        // On that event the reducer sets status to 'answering', but we still need
        // to sync the URL and storage. We use a one-shot onEvent intercept via
        // a local sessionId capture variable and the onComplete reconcile path.
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

        // Capture whether we've seen session_started so we can redirect.
        // We use a mutable ref-like closure variable (does not need React state).
        let resolvedSessionId: string | null = null;

        const cancel = intakeStream(
          { mode: 'create', body: { initial_request: initialRequest, projects, tz } },
          {
            onEvent: (evt: IntakeSSEEvent) => {
              if (evt.event === 'session_started') {
                resolvedSessionId = evt.data.session_id;
                // The session now exists — upload any files queued on the form.
                if (files.length > 0) void uploadAttachments(evt.data.session_id, files);
                // Persist + URL-sync as soon as we have the id.
                if (user?.id) storage.saveActiveSession(evt.data.session_id);
                syncUrl(evt.data.session_id);
              }
              setState((prev) => handleIntakeEvent(prev, evt));
            },
            onNonStream: () => {
              // Server didn't stream — fall back to REST silently.
              setState((prev) => ({ ...prev, stepRunning: false, status: 'idle' }));
            },
            onHttpError: ({ status, detail }) => {
              if (status === 401) return;
              setStepError('start', detail, status >= 500 || status === 429);
              setState((prev) => ({ ...prev, status: 'idle', stepRunning: false }));
            },
            onStreamError: (err) => {
              setStepError('start', err.message, true);
              setState((prev) => ({ ...prev, status: 'idle', stepRunning: false }));
            },
            onComplete: () => {
              streamTeardownRef.current = null;
              if (resolvedSessionId) {
                void reconcileFromGet(resolvedSessionId);
              } else {
                // Stream ended without a session_started — show error.
                setStepError('start', 'Stream ended without creating a session', true);
                setState((prev) => ({ ...prev, status: 'idle', stepRunning: false }));
              }
            },
          },
        );
        streamTeardownRef.current = cancel;
        // No lastActionRef here — streaming start is not retried via lastActionRef;
        // retry() will call reconcileFromGet or fall back via failed_step_id.
        return;
      }

      // Flag off — Phase B REST path.
      const action = async (): Promise<void> => {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
          const session = await intake.create({ initial_request: initialRequest, projects, tz });
          if (user?.id) storage.saveActiveSession(session.id);
          syncUrl(session.id);
          setState({
            ...initialState,
            status: session.status === 'preview' ? 'preview' : 'answering',
            session,
          });
          // The session now exists — upload any files queued on the form. Not awaited:
          // a failed upload shows on its row and never blocks the wizard.
          if (files.length > 0) void uploadAttachments(session.id, files);
        } catch (e) {
          const msg = e instanceof ApiError ? e.detail : 'Failed to start intake session';
          setStepError('start', msg, true);
          setState((prev) => ({ ...prev, status: 'idle' }));
        }
      };
      lastActionRef.current = action;
      await action();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      user?.id,
      storage,
      syncUrl,
      clearError,
      setStepError,
      reconcileFromGet,
      openIntakeStream,
      clearAttachments,
      uploadAttachments,
    ],
  );

  const submitAnswers = useCallback(
    async (
      answers: IntakeAnswerItem[],
      finalize = false,
      idempotency_key?: string,
    ): Promise<void> => {
      const session = state.session;
      if (!session) return;
      clearError();

      // Generate a stable idempotency key for this submission if none provided.
      const ikey = idempotency_key ?? crypto.randomUUID();
      // session.round is a 1-based count (len(rounds)).
      // The wire round_index expected by POST /answer/ is the 0-based open-round
      // index = session.round - 1. Math.max(0, ...) guards against a hypothetical
      // zero-round session that should never occur in practice.
      const round = Math.max(0, session.round - 1);

      // Store for retry.
      lastAnswerPayloadRef.current = { answers, finalize, idempotency_key: ikey, round };

      if (isIntakeStreamingEnabled()) {
        // Phase C — drive via SSE stream.
        // Clear draft answers before the stream opens.
        storage.clearDraftAnswers(session.id);
        setRestoredAnswers(null);

        // Show skeleton while the backend is working.
        setState((prev) => ({ ...prev, stepRunning: true }));

        openIntakeStream(
          {
            mode: 'answer',
            sessionId: session.id,
            body: {
              answers,
              finalize,
              idempotency_key: ikey,
              round_index: round,
            },
          },
          session.id,
        );
        // No lastActionRef — streaming submits reconcile via GET on done.
        return;
      }

      // Flag off — Phase B REST path.
      const action = async (): Promise<void> => {
        // Mark the step as processing so the page shows skeletons AND the
        // Sherlock buddy walks/narrates during the (20–40s) LLM wait. REST has
        // no SSE step_started to set this, so set it explicitly here.
        setState((prev) => ({ ...prev, stepRunning: true }));
        try {
          const updated = await intake.answer(session.id, answers, finalize, ikey, round);
          // Clear draft answers for this session on successful submit.
          storage.clearDraftAnswers(session.id);
          setRestoredAnswers(null);
          lastAnswerPayloadRef.current = null;
          setState((prev) => ({
            ...prev,
            status: updated.status === 'preview' ? 'preview' : 'answering',
            session: updated,
            stepRunning: false,
            error: null,
          }));
        } catch (e) {
          const msg = e instanceof ApiError ? e.detail : 'Failed to submit answers';
          const isRetriable = !(e instanceof ApiError && e.status === 409);
          setState((prev) => ({ ...prev, stepRunning: false }));
          setStepError('submitAnswers', msg, isRetriable);
        }
      };

      lastActionRef.current = action;
      await action();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.session, storage, clearError, setStepError, openIntakeStream],
  );

  const finalizeNow = useCallback(
    async (answers: IntakeAnswerItem[], idempotency_key?: string): Promise<void> =>
      submitAnswers(answers, true, idempotency_key),
    [submitAnswers],
  );

  const updatePreview = useCallback(
    async (patch: Partial<RequirementPreview>): Promise<void> => {
      const sessionId = state.session?.id;
      if (!sessionId) return;
      // Optimistic: caller (PreviewCard) has already updated local state.
      // Capture prev for rollback.
      const prevSession = state.session;
      try {
        const updated = await intake.patch(sessionId, patch);
        setState((prev) => ({ ...prev, session: updated, error: null }));
      } catch (e) {
        // Rollback by restoring previous session.
        setState((prev) => ({
          ...prev,
          session: prevSession,
          error: {
            phase: 'updatePreview',
            message: e instanceof ApiError ? e.detail : "Couldn't save — Retry",
            retriable: true,
          },
        }));
        // Re-throw so PreviewCard can rollback its local state too.
        throw e;
      }
    },
    [state.session],
  );

  const updateProjects = useCallback(
    async (projects: DetectProject[]): Promise<void> => {
      const sessionId = state.session?.id;
      if (!sessionId) return;
      const prevSession = state.session;
      try {
        const updated = await intake.patch(sessionId, { projects });
        setState((prev) => ({ ...prev, session: updated, error: null }));
      } catch (e) {
        setState((prev) => ({
          ...prev,
          session: prevSession,
          error: {
            phase: 'updateProjects',
            message: e instanceof ApiError ? e.detail : 'Failed to update projects',
            retriable: true,
          },
        }));
        throw e;
      }
    },
    [state.session],
  );

  const submit = useCallback(async (): Promise<void> => {
    const sessionId = state.session?.id;
    if (!sessionId) return;
    clearError();
    setState((prev) => ({ ...prev, status: 'deduping', dedupeCandidates: null }));

    const action = async (): Promise<void> => {
      try {
        const resp = await intake.dedupeCandidates(sessionId);
        if (resp.candidates.length === 0) {
          // Nothing similar — confirm directly (fire-and-forget 202, then poll).
          setState((prev) => ({ ...prev, status: 'confirming' }));
          try {
            await intake.confirm(sessionId, { link_to_requirement_id: null });
          } catch (e) {
            setState((prev) => ({
              ...prev,
              status: 'preview',
              error: {
                phase: 'confirm',
                message: e instanceof ApiError ? e.detail : 'Failed to confirm requirement',
                retriable: true,
              },
            }));
            return;
          }
          await pollConfirmUntilTerminal(sessionId, {
            onConfirmed: (session) => {
              if (user?.id) storage.clearActiveSession();
              storageClearDraftAnswers(sessionId);
              setState((prev) => ({
                ...prev,
                status: 'done',
                session,
                confirmResult: {
                  session_id: session.id,
                  status: 'confirmed',
                  notion_task_id: session.notion_task_id,
                  requirement_id: session.requirement_id,
                  notion_url: session.notion_url ?? null,
                  deduped_into: null,
                },
                error: null,
              }));
            },
            onFailed: (session) => {
              setState((prev) => ({
                ...prev,
                status: 'preview',
                session,
                error: {
                  phase: 'confirmRetry',
                  message: session.last_error ?? 'Failed to create the Notion task',
                  retriable: true,
                },
              }));
            },
            onDeadline: () => {
              setState((prev) => ({
                ...prev,
                status: 'preview',
                error: {
                  phase: 'confirmRetry',
                  message: 'Still creating the requirement — refresh to check status.',
                  retriable: true,
                },
              }));
            },
          });
        } else {
          setState((prev) => ({
            ...prev,
            dedupeCandidates: resp.candidates,
            error: null,
          }));
        }
      } catch (e) {
        // On failure, go back to preview and show inline error.
        setState((prev) => ({
          ...prev,
          status: 'preview',
          error: {
            phase: 'submit',
            message: e instanceof ApiError ? e.detail : 'Failed to submit request',
            retriable: true,
          },
        }));
      }
    };

    lastActionRef.current = action;
    await action();
  }, [state.session?.id, user?.id, storage, clearError]);

  const backToPreview = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'preview', dedupeCandidates: null, error: null }));
  }, []);

  const confirm = useCallback(
    async (linkToRequirementId: string | null): Promise<void> => {
      const sessionId = state.session?.id;
      if (!sessionId) return;
      clearError();
      setState((prev) => ({ ...prev, status: 'confirming' }));

      const action = async (): Promise<void> => {
        try {
          await intake.confirm(sessionId, { link_to_requirement_id: linkToRequirementId });
        } catch (e) {
          setState((prev) => ({
            ...prev,
            status: 'preview',
            error: {
              phase: 'confirm',
              message: e instanceof ApiError ? e.detail : 'Failed to confirm requirement',
              retriable: true,
            },
          }));
          return;
        }

        await pollConfirmUntilTerminal(sessionId, {
          onConfirmed: (session) => {
            if (user?.id) storage.clearActiveSession();
            storageClearDraftAnswers(sessionId);
            setState((prev) => ({
              ...prev,
              status: 'done',
              session,
              confirmResult: {
                session_id: session.id,
                status: 'confirmed',
                notion_task_id: session.notion_task_id,
                requirement_id: session.requirement_id,
                notion_url: session.notion_url ?? null,
                deduped_into: null,
              },
              error: null,
            }));
          },
          onFailed: (session) => {
            setState((prev) => ({
              ...prev,
              status: 'preview',
              session,
              error: {
                phase: 'confirmRetry',
                message: session.last_error ?? 'Failed to create the Notion task',
                retriable: true,
              },
            }));
          },
          onDeadline: () => {
            // Deadline hit without a terminal state — the task is still running server-side.
            setState((prev) => ({
              ...prev,
              status: 'preview',
              error: {
                phase: 'confirmRetry',
                message: 'Still creating the requirement — refresh to check status.',
                retriable: true,
              },
            }));
          },
        });
      };

      lastActionRef.current = action;
      await action();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.session?.id, user?.id, storage, clearError],
  );

  const retry = useCallback(async (): Promise<void> => {
    const sessionId = state.session?.id;
    clearError();

    const failedStep = state.session?.steps?.find(
      (s) => s.step_id === state.session?.failed_step_id,
    );

    // Non-confirm failed steps use the server-side retry endpoint.
    if (sessionId && state.session?.failed_step_id && failedStep?.kind !== 'confirm') {
      const action = async (): Promise<void> => {
        try {
          const updated = await intake.retryStep(sessionId);
          setState((prev) => ({
            ...prev,
            status: updated.status === 'preview' ? 'preview' : 'answering',
            session: updated,
            error: null,
          }));
        } catch (e) {
          setStepError(
            'retry',
            e instanceof ApiError ? e.detail : 'Retry failed',
            true,
          );
        }
      };
      lastActionRef.current = action;
      await action();
      return;
    }

    // confirm failure (or no failed_step_id) → re-invoke the last action (the
    // poll-aware confirm closure when the last action was a confirm).
    const action = lastActionRef.current;
    if (action) {
      await action();
      return;
    }

    // Streaming submits deliberately leave lastActionRef null, so a mid-stream
    // drop lands here with nothing to re-invoke. Doing nothing would clear the
    // error and fall straight back to the endless skeleton, so re-read the
    // session: either the runner finished and the questions appear, or the
    // stalled-session error is re-derived and the user still has an exit.
    if (sessionId) {
      await reconcileFromGet(sessionId);
    }
  }, [
    state.session?.id,
    state.session?.failed_step_id,
    state.session?.steps,
    clearError,
    setStepError,
    reconcileFromGet,
  ]);

  const discardCurrent = useCallback(async (): Promise<void> => {
    const sessionId = state.session?.id;
    if (!sessionId) {
      // No session — just reset.
      if (user?.id) storage.clearActiveSession();
      setState(initialState);
      navigate('/requests/new', { replace: true });
      return;
    }
    try {
      await intake.discard(sessionId);
    } catch {
      // If discard fails (e.g. already confirmed → 409), we still clear locally.
    }
    if (user?.id) storage.clearActiveSession();
    storageClearDraftAnswers(sessionId);
    clearAttachments();
    setState(initialState);
    navigate('/requests/new', { replace: true });
  }, [state.session?.id, user?.id, storage, navigate, clearAttachments]);

  const reset = useCallback(() => {
    const sessionId = state.session?.id;
    if (user?.id) storage.clearActiveSession();
    if (sessionId) storageClearDraftAnswers(sessionId);
    setRestoredAnswers(null);
    lastActionRef.current = null;
    lastAnswerPayloadRef.current = null;
    clearAttachments();
    setState(initialState);
    // Navigate back to bare /requests/new to clear the URL param.
    navigate('/requests/new', { replace: true });
  }, [state.session?.id, user?.id, storage, navigate, clearAttachments]);

  const sessionIdForAttachments = state.session?.id;
  const addAttachments = useCallback(
    (files: File[]): void => {
      if (sessionIdForAttachments) void uploadAttachments(sessionIdForAttachments, files);
    },
    [sessionIdForAttachments, uploadAttachments],
  );
  const removeAttachment = useCallback(
    (key: string): void => {
      if (sessionIdForAttachments) void removeUpload(sessionIdForAttachments, key);
    },
    [sessionIdForAttachments, removeUpload],
  );
  const retryAttachment = useCallback(
    (key: string): void => {
      if (sessionIdForAttachments) void retryUpload(sessionIdForAttachments, key);
    },
    [sessionIdForAttachments, retryUpload],
  );

  const onAnswersChange = useCallback(
    (sessionId: string, answerState: DraftAnswerState) => {
      storage.saveDraftAnswers(sessionId, answerState);
    },
    [storage],
  );

  return {
    state,
    restoredAnswers,
    start,
    submitAnswers,
    finalizeNow,
    updatePreview,
    updateProjects,
    submit,
    backToPreview,
    confirm,
    retry,
    discardCurrent,
    reset,
    onAnswersChange,
    attachments: attachmentItems,
    addAttachments,
    removeAttachment,
    retryAttachment,
  };
}
